import { Journey, NodeTriggerType, NodeType } from "Types/Journey";
import { DynamicCondition, JoinType } from "Types/Segment";

/**
 * Run all custom mappers based on Feature flags
 * @param param0
 * @returns
 */
export function runJourneyNodesMapper({ journey }: { journey: Journey }): Journey {
    let updatedJourney = transformFromLegacyToMultipleEventsTriggerNode(journey);
    updatedJourney = transformConditionalNode({
        journey: updatedJourney,
    });
    return updatedJourney;
}

/**
 * If the node was created with v1 version
 * create a ConditionList variable containing the conditionalType and the eventName extracted from previous version
 * @param journey
 * @returns Updated journey nodes
 */
function transformFromLegacyToMultipleEventsTriggerNode(journey: Journey): Journey {
    const nodeIndex = journey.flow.findIndex((n) => n.type == NodeType.Trigger);

    if (nodeIndex == -1) {
        return journey;
    }
    let updateNode = journey.flow[nodeIndex];

    if (updateNode.data.triggerType) {
        const { triggerType, eventName, ...otherFormFields } = updateNode.data;
        const computedEventName = [NodeTriggerType.RegalVoice, NodeTriggerType.Custom].includes(triggerType)
            ? eventName
            : triggerType;
        const conditionDefaults: DynamicCondition = {
            joinType: JoinType.ALL,
            conditions: [
                {
                    conditionalType: triggerType,
                    eventName: computedEventName,
                    eventOperator: "isSet",
                    filters: [],
                    lastEventOnly: false,
                },
            ],
        };
        const triggerFiltersEvents = [conditionDefaults];
        const mutatedData = {
            triggerFiltersEvents,
        };
        updateNode = {
            ...updateNode,
            data: {
                ...otherFormFields,
                ...mutatedData,
            },
        };
    }
    const flow = [...journey.flow];
    flow[nodeIndex] = updateNode;
    return {
        ...journey,
        flow,
    };
}

export function transformConditionalNode({ journey }: { journey: Journey }) {
    return transformLegacyToMultipleProperty(journey);
}

export function normalizeIsOneOfPropertyValues<T extends { filters?: Array<Record<string, unknown>> }>(data: T): T {
    if (!data.filters) {
        return data;
    }

    return {
        ...data,
        filters: data.filters.map((filter) =>
            filter.propertyOperator === "isOneOf" &&
            filter.propertyValue !== undefined &&
            !Array.isArray(filter.propertyValue)
                ? { ...filter, propertyValue: [filter.propertyValue] }
                : filter
        ),
    };
}

/**
 * Transforms a journey from legacy mode (plain) to multiple property shape
 */
function transformLegacyToMultipleProperty(journey: Journey): Journey {
    const transformedConditionalNodesFlow: any = journey.flow.map((node) => {
        if (node.type != NodeType.Conditional) {
            return node;
        }
        if (!node.data.complexConditional) {
            const {
                contactAttribute,
                propertyName,
                propertyOperator,
                propertyFieldId,
                propertyType,
                relativeDateValue,
                propertyValue,
                dateComparisonType,
                lastEventOnly,
                ...sharedFormValues
            } = node.data;
            let mutatedData = {
                ...sharedFormValues,
                complexConditional: true,
            };
            switch (node.data.conditionalType) {
                case "contactAttribute":
                case "triggeringEvent":
                    mutatedData = {
                        ...mutatedData,
                        ...(contactAttribute || propertyName || propertyOperator
                            ? {
                                  filters: [
                                      {
                                          propertyName: contactAttribute || propertyName,
                                          propertyOperator,
                                          propertyFieldId,
                                          propertyType,
                                          relativeDateValue,
                                          propertyValue,
                                          dateComparisonType,
                                      },
                                  ],
                              }
                            : {
                                  filters: [],
                              }),
                    };
                    break;

                case "customEvent":
                case "regalVoiceEvent":
                    mutatedData = {
                        ...mutatedData,
                        lastEventOnly,
                        eventOperator:
                            propertyName || (!propertyName && propertyOperator == "isSet") ? "isSet" : "isNotSet",
                        ...(propertyName
                            ? {
                                  filters: [
                                      {
                                          propertyName,
                                          propertyOperator,
                                          propertyFieldId,
                                          propertyType,
                                          relativeDateValue,
                                          propertyValue,
                                          dateComparisonType,
                                      },
                                  ],
                              }
                            : {
                                  filters: [],
                              }),
                    };
                    break;
            }

            // if propertyName set that means the filters contains at least one element
            // if propertyName is not set but the propertyOperator "isSet" we're checking the event existance
            // otherwise default to "isNotSet"
            const nodeMutated = {
                ...node,
                data: mutatedData,
            };
            return nodeMutated;
        } else {
            return node;
        }
    });
    return {
        ...journey,
        flow: transformedConditionalNodesFlow,
    };
}
